import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { faL } from "@fortawesome/free-solid-svg-icons";
import {
  Observable,
  catchError,
  map,
  mergeMap,
  switchMap,
  throwError,
} from "rxjs";
import {
  ApiFailResponse,
  AuthApiResponse,
  AuthToken,
  TenantUser,
} from "src/app/view-model/auth.view-model";
import { environment } from "src/environments/environment";
import { TokenStorageService } from "./token-storage.service";
import { DataService } from "src/app/services/data.services";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private _token!: string;
  hasLoggedOut = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private tokenStorage: TokenStorageService,
    private dataService: DataService
  ) {}

  login(username: string, password: string, databaseID: number) {
    return this.http
      .post<AuthApiResponse>(
        `${environment.existingCaseWorthyAPIURL}Auth/LoginStandard`,
        {
          databaseId: databaseID, // TODO: handle dynamic database id
          username: username,
          password: password,
          ipAddress: null,
          userAgent: navigator.userAgent,
          timeZoneOffset: new Date().getTimezoneOffset() / 60.0,
          twoFactorValue: null,
          userApplication: 7, //CommHub (CaseWorthy.Domain.Security.Models.UserApplicationType)
          userLoginType: 1, //Standard (CaseWorthy.Domain.Security.Models.UserLoginType)
        }
      )
      .pipe(
        map((user) => {
          this.dataService.currentUser = user.user;
          return user;
        }),
        mergeMap((i) =>
          this.http
            .post<AuthToken>(
              `${environment.nextGenAccountsAPIURL}auth/signinwithcookie`,
              null
            )
            .pipe(
              map((response) => {
                this.tokenStorage.saveAuthToken(response.accessToken);
                this.tokenStorage.saveRefreshToken(response.refreshToken);
                return i;
              }),
              catchError((error: ApiFailResponse, caught) => {
                return throwError(error);
              })
            )
        ),
        catchError((error: ApiFailResponse, caught) => {
          return throwError(error);
        })
      );
  }

  getLoginErrorMessage(codeNumber: number): string {
    return codeNumber == 2
      ? "Your username or password was not entered correctly." //LoginStatusCodes.BadUserOrPassword
      : codeNumber == 3
      ? "You do not have access to Login. Please contact your administrator." //LoginStatusCodes.UserNotAuthorized
      : codeNumber == 4
      ? "Your account has been locked for security reasons. Please contact your administrator " +
        "or try again in 30 minutes." //LoginStatusCodes.UserLockedOut
      : codeNumber == 5
      ? "Your Authorization Code was not entered correctly." //LoginStatusCodes.InvalidTwoFactorValue
      : codeNumber == 7
      ? "An error occurred processing two-factor authentication."
      : codeNumber == 10
      ? "There is no registered email address associated with this username." //LoginStatusCodes.NoUserEmail
      : codeNumber == 11
      ? "Your Security Answer was not entered correctly." //LoginStatusCodes.InvalidSecurityAnswer
      : codeNumber == 13
      ? "Your password must be at least eight characters long and use at least one number " +
        "and one letter." //LoginStatusCodes.PasswordHMISError
      : codeNumber == 14
      ? "Your password must be between 6 and 20 characters long and include one letter, " +
        "one number, and one special character" //LoginStatusCodes.PasswordValidityError
      : codeNumber == 15
      ? "Your password cannot use your username, the HMIS vendor name, or any common dictionary word " +
        "spelled forwards or backwards" //LoginStatusCodes.PasswordDictionaryError
      : codeNumber == 16
      ? "Your password cannot be one of the previous six passwords you've used." //LoginStatusCodes.PasswordDuplicateError
      : codeNumber == 17
      ? "An invalid login token was supplied."
      : codeNumber == 18
      ? "An invalid page identifier was supplied."
      : // NOTE on 19 LoginStatusCodes.BrowserHasOtherSession - This message is also present in SessionError.html
      codeNumber == 19
      ? "You have another CaseWorthy session open in the same browser. " +
        "If you continue, this other session will be terminated. Would you like to continue?"
      : codeNumber == 20
      ? "There is another active session for this CaseWorthy database that is using the same login credentials. " +
        "If you continue, this other session will be terminated. Would you like to continue?"
      : codeNumber == 101
      ? "An error occurred attempting to send you an email."
      : codeNumber == 102
      ? "An error occurred attempting to send you a text message."
      : "";
  }

  verifySession() {
    return this.tokenStorage.validateAccessToken_Expiry();
  }
  verifyRefreshToken() {
    return this.tokenStorage.validateRefreshToken_Expiry();
  }

  signInWithCookies(): Observable<any> {
    return this.http
      .post<AuthToken>(
        `${environment.nextGenAccountsAPIURL}auth/signinwithcookie`,
        null
      )
      .pipe(
        map((response) => {
          if (response) {
            this.tokenStorage.saveAuthToken(response.accessToken);
            this.tokenStorage.saveRefreshToken(response.refreshToken);
            // if (this.auth.verifySession()) {
            const userData: TenantUser = {
              userID: this.tokenStorage.getUserDataWithToken().nameid,
              databaseID: this.tokenStorage.getUserDataWithToken().DatabaseID,
              userApplication:
                this.tokenStorage.getUserDataWithToken().UserApplication,
              userLevel: this.tokenStorage.getUserDataWithToken().UserLevel,
              loginType: this.tokenStorage.getUserDataWithToken().LoginType,
              userName: this.tokenStorage.getUserDataWithToken().unique_name,
              // firstName: string;
              // lastName: string;
              // email: string;
              // cellPhone: string;
              organizationID:
                this.tokenStorage.getUserDataWithToken().SelectedOrganizationID,
              // organizationIDs: number[];
              roleID: this.tokenStorage.getUserDataWithToken().SelectedRoleID,
              roleIDs: this.tokenStorage.getUserDataWithToken().RoleIDs,
              providerID: this.tokenStorage.getUserDataWithToken().ProviderID,
              portalRoleID:
                this.tokenStorage.getUserDataWithToken().PortalRoleID,
              // cWSession: string;
              timeZoneOffset:
                this.tokenStorage.getUserDataWithToken().TimeZoneOffset,
              // isValid: boolean;
            };
            this.dataService.currentUser = userData;
          }
        }),
        catchError((error: ApiFailResponse, caught) => {
          this.router.navigate(["unauthorized-access"]);
          return throwError(error);
        })
      );
  }

  logout() {
    return this.http
      .post<boolean>(`${environment.nextGenAccountsAPIURL}Auth/Logout`, null)
      .pipe((success) => {
        if (success) {
          this.tokenStorage.signOut();
          this.removeCurrentUser();
        }
        return success;
      });
  }

  removeCurrentUser(): void {
    // localStorage.removeItem('user')
    this.hasLoggedOut = true;
      this.router.navigate(["unauthorized-access"]);
  }
}
